import { Enum, Label, _decorator } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import NumberPrecision from 'number-precision';
import Game from '../../../Game';
import I18n from '../../../I18n/I18n';
import Account, { Unit } from '../../../Account';
import GameDependency from '../../../System/GameDependency';
import { fillTextArgument } from '../../Utils/Utility';
import Parameter from '../../Utils/Parameter';

enum EventType {
    VALUE_CHANGED = 'VALUE_CHANGED',
    TEXT_DISPLAY_UPDATED = 'TEXT_DISPLAY_UPDATED',
}

interface IAbbreviationConfig {
    sign: string;
    multiplier: number;
}

export interface INumberFormat {
    thousands: string;
    decimal: string;
}

export enum NumberFormatMode {
    CommaDot,
    DotComma,
    SpaceDot,
    SpaceComma,
    DotSpace,
    CommaSpace,
    EmptyComma,
    EmptyDot,
    EmptySpace,
}

export interface IFormatOption {
    displayDecimal: boolean;
    displayDecimalZero: boolean;
    forcedDisplayDecimalZero: boolean;
    displayLeadingZero: boolean;
    displayThousands: boolean;
    displayZero: boolean;
    displaySpecialZero: boolean;
    displayAbbreviation: boolean;
    displayDecimalWhenAbbreviation: boolean;
    abbreviationLowerLimit: number;
    abbreviationUpperLimit: number;
    isCurrency: boolean;
    useSpecificCurrencyUnit: boolean;
    specficCurrencyUnit: Unit;
    displayUnitSign: boolean;
    displayCurrencySign: boolean;
    displayMinusBeforeSign: boolean;
    useSingleCharSign: boolean;
    maxIntegerLength: number;
    minIntegerLength: number;
    maxDecimalLength: number;
    specialZeroText: string;
    signTemplate: string;
    textTemplate: string;
    localizedTextTemplate: string;
    customizedTextTemplate: (result: string) => string;
    customizedAbbreviationCheck: (value: number, formatOption: Readonly<IFormatOption>) => boolean;
    customizedAbbreviationChoice: (
        value: number,
        formatOption: Readonly<IFormatOption>,
        configList: Readonly<Readonly<IAbbreviationConfig>[]>,
    ) => IAbbreviationConfig;
    customizedNumberFormat: () => INumberFormat;
    customizedValueConverter: (value: number, formatOption: Readonly<IFormatOption>) => number;
}

const { ccclass, property, requireComponent, executeInEditMode } = _decorator;
const THOUSAND = 1000;
const MILLION = 1000000;
const BILLION = 1000000000;
const TRILLION = 1000000000000;
const abbreviationConfigList: IAbbreviationConfig[] = [
    { sign: 'K', multiplier: THOUSAND },
    { sign: 'M', multiplier: MILLION },
    { sign: 'B', multiplier: BILLION },
    { sign: 'T', multiplier: TRILLION },
].sort((a, b) => b.multiplier - a.multiplier); // sort by multiplier in descending order
const defaultAbbreviationCheck: IFormatOption['customizedAbbreviationCheck'] = (value: number, formatOption: IFormatOption) =>
    value >= formatOption.abbreviationLowerLimit;
const defaultAbbreviationChoice: IFormatOption['customizedAbbreviationChoice'] = (
    value: number,
    formatOption: IFormatOption,
    configList: IAbbreviationConfig[],
) =>
    configList.find((config) => {
        if (config.multiplier > formatOption.abbreviationUpperLimit) {
            return false;
        }
        return value >= config.multiplier;
    });

NumberPrecision.enableBoundaryChecking(false);

function resolveNumberFormatMode(format: NumberFormatMode): INumberFormat {
    switch (format) {
        case NumberFormatMode.CommaDot:
            return {
                thousands: ',',
                decimal: '.',
            };
        case NumberFormatMode.DotComma:
            return {
                thousands: '.',
                decimal: ',',
            };
        case NumberFormatMode.CommaSpace:
            return {
                thousands: ',',
                decimal: ' ',
            };
        case NumberFormatMode.DotSpace:
            return {
                thousands: '.',
                decimal: ' ',
            };
        case NumberFormatMode.SpaceComma:
            return {
                thousands: ' ',
                decimal: ',',
            };
        case NumberFormatMode.SpaceDot:
            return {
                thousands: ' ',
                decimal: '.',
            };
        case NumberFormatMode.EmptyComma:
            return {
                thousands: '',
                decimal: ',',
            };
        case NumberFormatMode.EmptyDot:
            return {
                thousands: '',
                decimal: '.',
            };
        case NumberFormatMode.EmptySpace:
            return {
                thousands: '',
                decimal: ' ',
            };
        default:
            return {
                thousands: ',',
                decimal: '.',
            };
    }
}

@ccclass
@requireComponent(Label)
@executeInEditMode
export default class NumberFormatter extends GameDependency implements IFormatOption {
    public static readonly EVENT_TYPE = EventType;
    public static numberFormat: INumberFormat = (() => {
        const format = +Parameter.get('nf', `${NumberFormatMode.CommaDot}`);
        return resolveNumberFormatMode(format);
    })();

    @property({ tooltip: DEV && '顯示小數' })
    public displayDecimal: boolean = false;

    @property({ tooltip: DEV && '小數點不足的位數補 0' })
    public displayDecimalZero: boolean = false;

    @property({
        tooltip: DEV && '以另外的Label取代顯示小數, 含小數點符號',
        visible(this: NumberFormatter) {
            return this.displayDecimal;
        },
    })
    public replaceDecimalWithLabel: boolean = false;

    @property({
        type: Label,
        tooltip: DEV && '取代小數顯示的Label',
        visible(this: NumberFormatter) {
            return this.replaceDecimalWithLabel;
        },
    })
    public decimalLabel: Label = null;

    @property({ tooltip: DEV && '整數不足的位數補 0' })
    public displayLeadingZero: boolean = false;

    @property({ tooltip: DEV && '每三位數加入逗號' })
    public displayThousands: boolean = false;

    @property({ tooltip: DEV && '是否顯示 0' })
    public displayZero: boolean = false;

    @property({ tooltip: DEV && '使用縮寫' })
    public displayAbbreviation: boolean = false;

    @property({
        tooltip: DEV && '在未使用 Display Decimal 時讓縮寫顯示小數點',
    })
    public displayDecimalWhenAbbreviation: boolean = false;

    @property({ tooltip: DEV && '超過此設定值後使用縮寫', min: THOUSAND })
    public abbreviationLowerLimit: number = THOUSAND;

    @property({ tooltip: DEV && '不使用超過此設定值的縮寫' })
    public abbreviationUpperLimit: number = -1;

    @property({ tooltip: DEV && '是否為貨幣' })
    public isCurrency: boolean = false;

    @property({ tooltip: DEV && '是否使用特定貨幣單位' })
    public useSpecificCurrencyUnit: boolean = false;

    @property({
        type: Enum(Unit),
        tooltip: DEV && '特定貨幣單位',
        visible(this: NumberFormatter) {
            return this.useSpecificCurrencyUnit;
        },
    })
    public specficCurrencyUnit: Unit = Unit.Credit;

    @property({ tooltip: DEV && '是否顯示單位符號' })
    public displayUnitSign: boolean = false;

    @property({ tooltip: DEV && '是否顯示貨幣符號' })
    public displayCurrencySign: boolean = false;

    @property({ tooltip: DEV && '負數時，是否將負號顯示在符號前方' })
    public displayMinusBeforeSign: boolean = false;

    @property({ tooltip: DEV && '是否使用單字元符號' })
    public useSingleCharSign: boolean = false;

    @property({ tooltip: DEV && '是否顯示特殊零' })
    public displaySpecialZero: boolean = false;

    @property({ tooltip: DEV && '整數最高位數，用於計算顯示 LeadingZero 時的最大位數' })
    public maxIntegerLength: number = 0;

    @property({ tooltip: DEV && '整數最低位數，用於計算顯示 LeadingZero 時的最小位數' })
    public minIntegerLength: number = -1;

    @property({ tooltip: DEV && '小數最高位數' })
    public maxDecimalLength: number = 0;

    @property({ tooltip: DEV && '特殊零字串' })
    public specialZeroText: string = '';

    @property({ tooltip: DEV && '可指定任意符號格式，並以 {0} 指定符號位置、{1} 指定 Value 位置，此字串不為空時會套用', multiline: true })
    public signTemplate: string = '{0}{1}';

    @property({ tooltip: DEV && '可指定任意文字格式，並以 {0} 指定 Value 位置，此字串不為空，且 localizedTextTemplate 未套用時會套用', multiline: true })
    public textTemplate: string = '';

    @property({ tooltip: DEV && '可指定任意多語系格式，並以 {0} 指定 Value 位置，此字串不為空時會套用 (編輯器中無法生效)', multiline: true })
    public localizedTextTemplate: string = '';

    @property
    private _value: number = 0;

    @property
    private _disabled: boolean = false;

    public forcedDisplayDecimalZero: boolean = false;

    private _label: Label = null;

    private _customizedTextTemplate: (result: string) => string = null;
    private _customizedAbbreviationCheck: IFormatOption['customizedAbbreviationCheck'] = defaultAbbreviationCheck;
    private _customizedAbbreviationChoice: IFormatOption['customizedAbbreviationChoice'] = defaultAbbreviationChoice;
    private _customizedNumberFormat: () => INumberFormat = null;
    private _customizedValueConverter: (value: number, formatOption: Readonly<IFormatOption>) => number = null;

    @property
    public set value(value: number) {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            throw new Error(`invalid value: ${value}`);
        }
        if (this._value === value) {
            return;
        }
        this._value = value;
        this.updateTextDisplay();
        this.emit(EventType.VALUE_CHANGED, value);
    }
    public get value() {
        return this._value;
    }

    /**
     * disable text updating, which means the text won't be updated by setting value, but value will still be stored.
     */
    @property
    public get disabled() {
        return this._disabled;
    }
    public set disabled(value: boolean) {
        if (this._disabled === value) {
            return;
        }
        this._disabled = value;
        this.updateTextDisplay();
    }

    public get customizedTextTemplate() {
        return this._customizedTextTemplate;
    }
    public set customizedTextTemplate(value: (result: string) => string) {
        if (this._customizedTextTemplate === value) {
            return;
        }
        this._customizedTextTemplate = value;
        this.updateTextDisplay();
    }

    public get customizedAbbreviationCheck() {
        return this._customizedAbbreviationCheck;
    }
    public set customizedAbbreviationCheck(value: IFormatOption['customizedAbbreviationCheck']) {
        if (this._customizedAbbreviationCheck === value) {
            return;
        }
        this._customizedAbbreviationCheck = value;
        this.updateTextDisplay();
    }

    public get customizedAbbreviationChoice() {
        return this._customizedAbbreviationChoice;
    }
    public set customizedAbbreviationChoice(value: IFormatOption['customizedAbbreviationChoice']) {
        if (!value) {
            throw new Error('empty abbreviation choice callback is not allowed');
        }
        if (this._customizedAbbreviationChoice === value) {
            return;
        }
        this._customizedAbbreviationChoice = value;
        this.updateTextDisplay();
    }

    public get customizedNumberFormat() {
        return this._customizedNumberFormat;
    }
    public set customizedNumberFormat(value: () => INumberFormat) {
        if (this._customizedNumberFormat === value) {
            return;
        }
        this._customizedNumberFormat = value;
        this.updateTextDisplay();
    }

    public get customizedValueConverter() {
        return this._customizedValueConverter;
    }
    public set customizedValueConverter(value: (value: number, formatOption: Readonly<IFormatOption>) => number) {
        if (this._customizedValueConverter === value) {
            return;
        }
        this._customizedValueConverter = value;
        this.updateTextDisplay();
    }

    public get label() {
        if (!this._label) {
            this._label = this.getComponent(Label);
        }
        return this._label;
    }

    public static format(value: number, formatOption: Partial<IFormatOption>, gameInstance?: Game): string {
        return this._formatWithCompleteOption(value, this._validAndCompleteFormatOption(formatOption), gameInstance);
    }

    public static resolveNumberFormatMode(format: NumberFormatMode): INumberFormat {
        return resolveNumberFormatMode(format);
    }

    private static _formatWithCompleteOption(value: number, formatOption: IFormatOption, gameInstance?: Game): string {
        const game = gameInstance ?? (EDITOR ? null : Game.Instance);
        const {
            displayDecimal,
            displayDecimalZero,
            displayLeadingZero,
            displayThousands,
            displayZero,
            displaySpecialZero,
            displayAbbreviation,
            displayDecimalWhenAbbreviation,
            isCurrency,
            useSpecificCurrencyUnit,
            specficCurrencyUnit,
            displayUnitSign,
            displayCurrencySign,
            displayMinusBeforeSign,
            useSingleCharSign,
            maxIntegerLength,
            minIntegerLength,
            maxDecimalLength,
            forcedDisplayDecimalZero,
            signTemplate,
            specialZeroText,
            localizedTextTemplate,
            textTemplate,
            customizedTextTemplate,
            customizedAbbreviationCheck,
            customizedAbbreviationChoice,
            customizedNumberFormat,
            customizedValueConverter,
        } = formatOption;

        if (customizedValueConverter) {
            value = customizedValueConverter(value, formatOption);
        }

        // 是否顯示 0
        if (value === 0) {
            if (displaySpecialZero) {
                return specialZeroText;
            }
            if (!displayZero) {
                return '';
            }
        }

        if (isCurrency && !EDITOR && game?.account) {
            if (useSpecificCurrencyUnit) {
                value = game.account.valueTo(value, specficCurrencyUnit);
            } else {
                value = game.account.valueTo(value);
            }
        }

        // TODO split to calculateValue & addDecorators methods
        const needAbbreviation = displayAbbreviation && (customizedAbbreviationCheck?.(value, formatOption) ?? true);
        let abbreviationConfig: IAbbreviationConfig = null;

        if (needAbbreviation) {
            // find the abbreviation config that value is in the range and has the largest multiplier
            abbreviationConfig = customizedAbbreviationChoice(value, formatOption, abbreviationConfigList);
            if (!abbreviationConfig) {
                throw new Error(`Can't find proper abbreviation config for value: ${value}`);
            }
            value = NumberPrecision.divide(value, abbreviationConfig.multiplier);
        }

        // fraction part

        // 固定顯示小數點後幾位
        let decimalDigits = displayDecimal || (needAbbreviation && displayDecimalWhenAbbreviation) ? maxDecimalLength : 0;
        const forceMaxDecimalDigits = game?.config.forceMaxDecimalDigits;
        if (forceMaxDecimalDigits !== null && forceMaxDecimalDigits !== undefined) {
            decimalDigits = game.config.forceMaxDecimalDigits;
        }
        let resultText =
            decimalDigits === -1
                ? value.toString()
                : (() => {
                      const factor = 10 ** decimalDigits;
                      return NumberPrecision.divide(Math.trunc(NumberPrecision.times(value, factor)), factor).toFixed(decimalDigits);
                  })();

        // 清除小數點的 0
        if (!forcedDisplayDecimalZero && (!displayDecimalZero || (!EDITOR && game?.config.hideZero))) {
            resultText = resultText.replace(/(\d+\.\d*[1-9]|\d+)(\.?0*)/, '$1');
        }

        // integer part

        // 前面添加 0
        if (displayLeadingZero) {
            const integerPart: RegExpMatchArray = resultText.match(/\d+/);
            const targetLength = minIntegerLength > 0 ? minIntegerLength : maxIntegerLength;
            const padStartZeroCount = targetLength - integerPart[0].length;
            if (padStartZeroCount > 0) {
                resultText = resultText.replace(/(\d+)/, `${'0'.repeat(padStartZeroCount)}$1`);
            }
        }

        // general

        // 每三位數加入逗號
        if (displayThousands) {
            const texts: string[] = resultText.split('.');
            resultText = texts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,') + (texts[1] ? `.${texts[1]}` : '');
        }

        // add currency or unit sign
        if (!EDITOR && game && !game.config.hideCurrency && (displayCurrencySign || displayUnitSign)) {
            const sign = useSingleCharSign ? game.account.singleCharCurrencySign : game.account.sign;
            const prefix = game.account.prefixSign;
            if (prefix && displayMinusBeforeSign && resultText.startsWith('-')) {
                const absText = resultText.slice(1);
                const signedText = fillTextArgument(signTemplate, [sign, absText]);
                resultText = `-${signedText}`;
            } else {
                resultText = fillTextArgument(signTemplate, prefix ? [sign, resultText] : [resultText, sign]);
            }
        }

        // add abbreviation sign
        if (abbreviationConfig) {
            resultText += abbreviationConfig.sign;
        }

        // apply text template
        if (customizedTextTemplate) {
            resultText = customizedTextTemplate(resultText);
        } else if (localizedTextTemplate !== '' && !EDITOR) {
            const message = game?.i18n.getMessage(localizedTextTemplate);
            resultText = fillTextArgument(message, [resultText]);
        } else if (textTemplate !== '') {
            resultText = fillTextArgument(textTemplate, [resultText]);
        }

        // 千份位 小數點 符號更換
        if (!EDITOR) {
            let nf = this.numberFormat;
            if (customizedNumberFormat) {
                nf = customizedNumberFormat();
            }
            resultText = resultText
                .replace(/,/g, '__COMMA__') // 暫存原本的 ,
                .replace(/\./g, '__DOT__') // 暫存原本的 .
                .replace(/__COMMA__/g, `${nf.thousands}`) // 把 , 換成 .
                .replace(/__DOT__/g, `${nf.decimal}`); // 把 . 換成 ,
        }

        return resultText;
    }

    private static _validAndCompleteFormatOption(formatOption: Partial<IFormatOption>): IFormatOption {
        const {
            displayDecimal = false,
            displayDecimalZero = false,
            forcedDisplayDecimalZero = false,
            displayLeadingZero = false,
            displayThousands = false,
            displayZero = false,
            displaySpecialZero = false,
            displayAbbreviation = false,
            displayDecimalWhenAbbreviation = false,
            isCurrency = false,
            useSpecificCurrencyUnit = false,
            specficCurrencyUnit = Unit.Credit,
            displayUnitSign = false,
            displayCurrencySign = false,
            displayMinusBeforeSign = false,
            useSingleCharSign = false,
            maxIntegerLength = 0,
            minIntegerLength = -1,
            maxDecimalLength = 0,
            signTemplate = '',
            specialZeroText = '',
            localizedTextTemplate = '',
            textTemplate = '',
            customizedTextTemplate = null,
            customizedAbbreviationCheck = defaultAbbreviationCheck,
            customizedAbbreviationChoice = defaultAbbreviationChoice,
            customizedNumberFormat = null,
            customizedValueConverter = null,
        } = formatOption;
        let { abbreviationLowerLimit = THOUSAND, abbreviationUpperLimit = -1 } = formatOption;
        // abbreviation limit must large or equal to 1000 at least, otherwise it will be no sense at all
        abbreviationLowerLimit = Math.max(THOUSAND, abbreviationLowerLimit);
        abbreviationUpperLimit = abbreviationUpperLimit === -1 ? Number.MAX_SAFE_INTEGER : Math.max(THOUSAND, abbreviationUpperLimit);
        return {
            displayDecimal,
            displayDecimalZero,
            forcedDisplayDecimalZero,
            displayLeadingZero,
            displayThousands,
            displayZero,
            displaySpecialZero,
            displayAbbreviation,
            displayDecimalWhenAbbreviation,
            isCurrency,
            useSpecificCurrencyUnit,
            specficCurrencyUnit,
            displayUnitSign,
            displayCurrencySign,
            displayMinusBeforeSign,
            useSingleCharSign,
            maxIntegerLength,
            maxDecimalLength,
            minIntegerLength,
            signTemplate,
            specialZeroText,
            localizedTextTemplate,
            textTemplate,
            customizedTextTemplate,
            customizedAbbreviationCheck,
            customizedAbbreviationChoice,
            customizedNumberFormat,
            customizedValueConverter,
            abbreviationLowerLimit,
            abbreviationUpperLimit,
        };
    }

    public onEnable() {
        if (!EDITOR) {
            this.game?.account.on(
                Account.EVENT_TYPE.UNIT_CHANGED,
                () => {
                    if (this.displayUnitSign || this.isCurrency) {
                        this.updateTextDisplay();
                    }
                },
                this,
            );
            this.game?.account.on(
                Account.EVENT_TYPE.CURRENCY_CHANGED,
                () => {
                    if (this.displayCurrencySign) {
                        this.updateTextDisplay();
                    }
                },
                this,
            );
            this.game?.account.on(
                Account.EVENT_TYPE.DENOM_CHANGED,
                () => {
                    if (this.isCurrency) {
                        this.updateTextDisplay();
                    }
                },
                this,
            );
            this.game?.i18n.on(
                I18n.EVENT_TYPE.LanguageChanged,
                () => {
                    if (this.localizedTextTemplate || this.customizedTextTemplate) {
                        this.updateTextDisplay();
                    }
                },
                this,
            );
            // 停用期間錯過的事件，重新啟用時補刷新
            this.updateTextDisplay();
        }
    }

    public onDisable() {
        if (!EDITOR) {
            this.game?.account.targetOff(this);
            this.game?.i18n.targetOff(this);
        }
    }

    public start() {
        this.updateTextDisplay();
    }

    public updateTextDisplay() {
        if (this.disabled) {
            return;
        }
        const result = NumberFormatter.format(this.value, this, this.game);
        if (this.replaceDecimalWithLabel) {
            const findIndex = result.indexOf(NumberFormatter.numberFormat.decimal);
            const decimalWithSign = result.substring(findIndex !== -1 ? findIndex : result.length, result.length);
            const integerPart = result.substring(0, findIndex !== -1 ? findIndex : result.length);
            this.decimalLabel.string = decimalWithSign;
            this.label.string = integerPart;
        } else {
            this.label.string = result;
        }
        this.emit(EventType.TEXT_DISPLAY_UPDATED, result);
    }
}
